<script setup lang="ts">
// Curated YouTube videos via the privacy-enhanced nocookie embed.
// Thumbnails are static <img>; the iframe loads only after a click, keeping
// the page fast and third-party-free until the visitor opts in.
import { ref } from 'vue'

export interface VideoItem {
    id: string
    title: string
    blurb?: string
}

defineProps<{ videos: VideoItem[] }>()

const playing = ref<string | null>(null)
</script>

<template>
    <div class="video-grid">
        <div v-for="video in videos" :key="video.id" class="glass video-card">
            <div class="frame">
                <iframe
                    v-if="playing === video.id"
                    :src="`https://www.youtube-nocookie.com/embed/${video.id}?autoplay=1`"
                    :title="video.title"
                    frameborder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowfullscreen
                ></iframe>
                <button v-else class="thumb" type="button" :aria-label="`Play: ${video.title}`" @click="playing = video.id">
                    <img
                        :src="`https://i.ytimg.com/vi/${video.id}/hqdefault.jpg`"
                        :alt="video.title"
                        loading="lazy"
                    />
                    <span class="play">▶</span>
                </button>
            </div>
            <h3>{{ video.title }}</h3>
            <p v-if="video.blurb" class="muted">{{ video.blurb }}</p>
        </div>
    </div>
</template>

<style scoped lang="scss">
.video-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: var(--site-space-lg);
}

.video-card {
    padding: var(--site-space-md);

    h3 {
        font-size: 1.05rem;
        margin: var(--site-space-md) 0 var(--site-space-xs);
    }

    p {
        font-size: 0.9rem;
        margin: 0;
    }
}

.frame {
    position: relative;
    aspect-ratio: 16 / 9;
    border-radius: var(--site-radius-md);
    overflow: hidden;
    background: var(--site-bg-alt);

    iframe,
    .thumb {
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
    }
}

.thumb {
    display: block;
    border: none;
    padding: 0;
    cursor: pointer;
    background: none;

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }

    .play {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 3.25rem;
        height: 3.25rem;
        display: grid;
        place-items: center;
        border-radius: 50%;
        background: var(--site-accent);
        color: var(--site-accent-contrast);
        font-size: 1.1rem;
        padding-left: 3px;
        transition: transform 0.15s ease;
    }

    &:hover .play {
        transform: translate(-50%, -50%) scale(1.1);
        box-shadow: var(--site-glow);
    }
}
</style>
